import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Avatar,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  InputBase,
  Link,
  Paper,
  Slide,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ThumbUpOffAltIcon from '@mui/icons-material/ThumbUpOffAlt';
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt';
import ThumbDownOffAltIcon from '@mui/icons-material/ThumbDownOffAlt';
import ThumbDownAltIcon from '@mui/icons-material/ThumbDownAlt';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import PersonIcon from '@mui/icons-material/Person';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import helpAssistantService from '../../services/helpAssistantService';
import { getModuleFromPath, getModuleLabel } from './moduleContext';
import { renderMarkdown } from './markdown';

const STORAGE_KEY = 'biztimize.helpAssistant.v1';
const MAX_HISTORY_TURNS = 12;

function loadPersistedSession() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.messages)) return null;
    return parsed;
  } catch {
    return null;
  }
}

function persistSession(session) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  } catch {
    /* storage full / disabled — silently ignore */
  }
}

function buildHistory(messages) {
  return messages
    .filter((m) => m.role === 'user' || m.role === 'assistant')
    .filter((m) => !m.pending && !m.error)
    .slice(-MAX_HISTORY_TURNS)
    .map((m) => ({ role: m.role, content: m.text || '' }));
}

function SourcesList({ sources }) {
  if (!sources || !sources.length) return null;
  return (
    <Box sx={{ mt: 1.2, pt: 1, borderTop: '1px dashed rgba(0,0,0,0.1)' }}>
      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
        Sources
      </Typography>
      <Box sx={{ mt: 0.5, display: 'flex', flexDirection: 'column', gap: 0.3 }}>
        {sources.map((src) => (
          <Link
            key={src.chunk_uid || `${src.article_slug}-${src.section_title}`}
            href={src.url || '#'}
            target="_blank"
            rel="noreferrer noopener"
            underline="hover"
            sx={{ fontSize: '0.78rem', display: 'flex', alignItems: 'center', gap: 0.5 }}
          >
            <OpenInNewIcon sx={{ fontSize: 12 }} />
            {src.article_title}
            {src.section_title ? ` — ${src.section_title}` : ''}
          </Link>
        ))}
      </Box>
    </Box>
  );
}

function FeedbackControls({ responseUid, currentRating, onRate }) {
  if (!responseUid) return null;
  return (
    <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Typography variant="caption" sx={{ color: 'text.secondary', mr: 0.5 }}>
        Was this helpful?
      </Typography>
      <IconButton
        size="small"
        onClick={() => onRate(responseUid, 1)}
        aria-label="mark helpful"
        sx={{ p: 0.3 }}
      >
        {currentRating === 1 ? (
          <ThumbUpAltIcon sx={{ fontSize: 16, color: 'success.main' }} />
        ) : (
          <ThumbUpOffAltIcon sx={{ fontSize: 16 }} />
        )}
      </IconButton>
      <IconButton
        size="small"
        onClick={() => onRate(responseUid, -1)}
        aria-label="mark unhelpful"
        sx={{ p: 0.3 }}
      >
        {currentRating === -1 ? (
          <ThumbDownAltIcon sx={{ fontSize: 16, color: 'error.main' }} />
        ) : (
          <ThumbDownOffAltIcon sx={{ fontSize: 16 }} />
        )}
      </IconButton>
    </Box>
  );
}

function MessageBubble({ message, onRate }) {
  const isUser = message.role === 'user';
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: isUser ? 'flex-end' : 'flex-start',
        mb: 1.2,
      }}
    >
      {!isUser && (
        <Avatar
          sx={{
            bgcolor: 'primary.main',
            width: 28,
            height: 28,
            mr: 1,
            mt: 0.2,
          }}
        >
          <SmartToyOutlinedIcon sx={{ fontSize: 16 }} />
        </Avatar>
      )}
      <Box
        sx={{
          maxWidth: '82%',
          bgcolor: isUser ? 'primary.main' : theme.palette.grey[100],
          color: isUser ? 'primary.contrastText' : 'text.primary',
          px: 1.3,
          py: 0.9,
          borderRadius: isUser
            ? '14px 14px 2px 14px'
            : '14px 14px 14px 2px',
          boxShadow: isUser ? 'none' : '0 1px 2px rgba(0,0,0,0.05)',
          fontSize: '0.88rem',
        }}
      >
        {message.pending ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={14} sx={{ color: 'text.secondary' }} />
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Reading the docs…
            </Typography>
          </Box>
        ) : (
          <Box sx={{ '& p:last-child': { mb: 0 } }}>
            {isUser ? (
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {message.text}
              </Typography>
            ) : (
              renderMarkdown(message.text)
            )}
          </Box>
        )}
        {!isUser && !message.pending && (
          <>
            <SourcesList sources={message.sources} />
            <FeedbackControls
              responseUid={message.responseUid}
              currentRating={message.rating}
              onRate={onRate}
            />
          </>
        )}
      </Box>
      {isUser && (
        <Avatar
          sx={{
            bgcolor: 'grey.400',
            width: 28,
            height: 28,
            ml: 1,
            mt: 0.2,
          }}
        >
          <PersonIcon sx={{ fontSize: 16 }} />
        </Avatar>
      )}
    </Box>
  );
}

export default function HelpAssistantWidget({ open, onClose }) {
  const location = useLocation();
  const moduleTag = useMemo(() => getModuleFromPath(location.pathname), [location.pathname]);
  const moduleLabel = useMemo(() => getModuleLabel(moduleTag), [moduleTag]);

  const [pristine, setPristine] = useState(true); // true until user interacts
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [session, setSession] = useState(() => {
    const persisted = loadPersistedSession();
    return (
      persisted || {
        sessionUid: null,
        messages: [
          {
            role: 'assistant',
            text:
              "Hi! I'm the Biztimize Help Assistant. Ask me anything about the ERP — how to create a sales order, apply for leave, raise a PO, and more. I'll answer from the official help documentation.",
            welcome: true,
          },
        ],
      }
    );
  });
  const scrollRef = useRef(null);

  // Persist session changes
  useEffect(() => {
    persistSession(session);
  }, [session]);

  // Auto-scroll to bottom on new message
  useEffect(() => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [session.messages, open]);

  // Pull suggestions when the panel opens or the module context changes
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    helpAssistantService
      .getSuggestions(moduleTag)
      .then((res) => {
        if (!cancelled) setSuggestions(res?.data || []);
      })
      .catch(() => {
        if (!cancelled) setSuggestions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open, moduleTag]);

  const handleSend = useCallback(
    async (explicitText) => {
      const text = (explicitText ?? input).trim();
      if (!text || sending) return;
      setPristine(false);
      setInput('');

      const userMessage = { role: 'user', text };
      const pendingMessage = {
        role: 'assistant',
        text: '',
        pending: true,
        tempId: `pending-${Date.now()}`,
      };
      const baseMessages = [...session.messages, userMessage, pendingMessage];
      setSession((s) => ({ ...s, messages: baseMessages }));
      setSending(true);

      try {
        const history = buildHistory(session.messages);
        const payload = {
          query: text,
          session_uid: session.sessionUid || undefined,
          module_context: moduleTag || '',
          page_path: location.pathname,
          history,
        };
        const res = await helpAssistantService.chat(payload);
        const data = res?.data || {};
        setSession((s) => ({
          ...s,
          sessionUid: data.session_uid || s.sessionUid,
          messages: s.messages
            .filter((m) => m.tempId !== pendingMessage.tempId)
            .concat([
              {
                role: 'assistant',
                text: data.answer || '',
                sources: data.sources || [],
                responseUid: data.response_uid,
                grounded: data.grounded,
                rating: null,
              },
            ]),
        }));
      } catch (err) {
        const detail =
          err?.response?.data?.detail ||
          'The Help Assistant is temporarily unavailable. Please try again in a moment.';
        setSession((s) => ({
          ...s,
          messages: s.messages
            .filter((m) => m.tempId !== pendingMessage.tempId)
            .concat([{ role: 'assistant', text: detail, error: true }]),
        }));
      } finally {
        setSending(false);
      }
    },
    [input, sending, session.messages, session.sessionUid, moduleTag, location.pathname]
  );

  const handleRate = useCallback(async (responseUid, rating) => {
    setSession((s) => ({
      ...s,
      messages: s.messages.map((m) =>
        m.responseUid === responseUid ? { ...m, rating } : m
      ),
    }));
    try {
      await helpAssistantService.feedback({ response_uid: responseUid, rating });
    } catch {
      /* rating is a nice-to-have; don't block the UI on failure */
    }
  }, []);

  const handleReset = useCallback(() => {
    setSession({
      sessionUid: null,
      messages: [
        {
          role: 'assistant',
          text: 'New conversation started. What would you like help with?',
          welcome: true,
        },
      ],
    });
    setPristine(true);
    setInput('');
  }, []);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      <Slide direction="up" in={open} mountOnEnter unmountOnExit>
        <Paper
          elevation={12}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            width: { xs: 'calc(100vw - 32px)', sm: 380 },
            height: { xs: '80vh', sm: 560 },
            maxHeight: 'calc(100vh - 48px)',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 3,
            overflow: 'hidden',
            zIndex: 1400,
          }}
        >
          <Box
            sx={{
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              px: 1.5,
              py: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Avatar sx={{ bgcolor: 'primary.dark', width: 30, height: 30 }}>
              <SmartToyOutlinedIcon sx={{ fontSize: 18 }} />
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle2" sx={{ lineHeight: 1.1, fontWeight: 700 }}>
                Biztimize Help Assistant
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.85, display: 'block', lineHeight: 1.1 }}>
                Context: {moduleLabel}
              </Typography>
            </Box>
            <Tooltip title="Start new conversation">
              <IconButton
                size="small"
                onClick={handleReset}
                sx={{ color: 'inherit' }}
                aria-label="Reset conversation"
              >
                <RestartAltIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Close">
              <IconButton
                size="small"
                onClick={onClose}
                sx={{ color: 'inherit' }}
                aria-label="Close Help Assistant"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          <Box
            ref={scrollRef}
            sx={{
              flex: 1,
              overflowY: 'auto',
              px: 1.3,
              py: 1,
              bgcolor: 'background.default',
            }}
          >
            {session.messages.map((m, i) => (
              <MessageBubble key={i} message={m} onRate={handleRate} />
            ))}

            {pristine && suggestions.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', ml: 0.5 }}>
                  Try asking:
                </Typography>
                <Box sx={{ mt: 0.6, display: 'flex', flexWrap: 'wrap', gap: 0.6 }}>
                  {suggestions.map((s) => (
                    <Chip
                      key={s.id}
                      label={s.question}
                      size="small"
                      onClick={() => handleSend(s.question)}
                      sx={{ cursor: 'pointer' }}
                    />
                  ))}
                </Box>
              </Box>
            )}
          </Box>

          <Box
            sx={{
              p: 1,
              borderTop: '1px solid rgba(0,0,0,0.08)',
              display: 'flex',
              alignItems: 'flex-end',
              gap: 0.5,
              bgcolor: 'background.paper',
            }}
          >
            <InputBase
              multiline
              minRows={1}
              maxRows={5}
              fullWidth
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask a question about Biztimize…"
              disabled={sending}
              sx={{
                px: 1,
                py: 0.4,
                fontSize: '0.9rem',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
              }}
              inputProps={{ 'aria-label': 'Ask the Biztimize Help Assistant' }}
            />
            <IconButton
              color="primary"
              disabled={!input.trim() || sending}
              onClick={() => handleSend()}
              aria-label="Send question"
            >
              {sending ? <CircularProgress size={18} /> : <SendIcon />}
            </IconButton>
          </Box>
          <Typography
            variant="caption"
            sx={{ px: 1.3, pb: 0.6, color: 'text.secondary', fontSize: '0.68rem' }}
          >
            Answers are generated from Biztimize help documentation. Your queries
            are logged for your tenant to improve the documentation.
          </Typography>
        </Paper>
      </Slide>
    </>
  );
}
